'use client'

import { useIntl } from 'react-intl'
import {
  Dialog,
  Cell,
  Text,
  Button,
  Divider,
  Navigation,
  BottomSheet,
  Validation,
} from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { EscrowRefundEstimateModel } from '@marketplace-web/complaints/complaints-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

type Props = {
  refundEstimate: EscrowRefundEstimateModel
  isOpenedInDialog?: boolean
  isOpen: boolean
  onClose: () => void
}

const PartialRefundOfferCurrencyConversionDialogNew = ({
  refundEstimate,
  isOpenedInDialog = false,
  isOpen,
  onClose,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate('partial_refund_offer_currency_conversion_dialog')
  const { phones } = useBreakpoint()
  const isMobileView = phones && !isOpenedInDialog

  const { beforeConversion } = refundEstimate
  const conversionElements = refundEstimate.beforeConversion?.conversionElements || []

  const renderNavigation = () => (
    <Navigation
      body={translate('title')}
      right={
        <Button
          inline
          iconName={X24}
          styling={Button.Styling.Flat}
          onClick={onClose}
          aria={{ 'aria-label': translate('a11y.close') }}
        />
      }
    />
  )

  const renderConversionElements = () =>
    conversionElements.map(element => {
      const price = formatCurrencyAmount(element.money, locale)

      return (
        <Cell
          key={element.title}
          title={element.title}
          body={
            <>
              {element.subtitle && (
                <Text as="span" type={Text.Type.Subtitle} text={element.subtitle} />
              )}
              {element.error && <Validation text={element.error} theme="warning" />}
            </>
          }
          suffix={price}
        />
      )
    })

  const renderExchangeRate = () => {
    if (!beforeConversion) return null

    const { baseRate, exchangeRate } = beforeConversion.exchange

    const base = formatCurrencyAmount(baseRate, locale)
    const rate = formatCurrencyAmount(exchangeRate, locale)

    return (
      <>
        <Divider />
        <Cell
          body={
            <Text
              as="span"
              text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
            />
          }
        />
      </>
    )
  }

  const renderPaymentServiceProviderFee = () => {
    if (!beforeConversion) return null

    const { rate, provider } = beforeConversion.markup

    const feePercentage = formatNumber(rate, {
      style: 'percent',
      maximumFractionDigits: 2,
    })

    return (
      <Cell
        body={
          <Text
            as="span"
            html
            type={Text.Type.Subtitle}
            text={translate('fx_markup_rate', { fee: feePercentage, payment_provider: provider })}
          />
        }
      />
    )
  }

  const renderActions = () => (
    <Cell
      body={
        <Button text={translate('actions.ok')} styling={Button.Styling.Filled} onClick={onClose} />
      }
    />
  )

  const renderContent = () => (
    <>
      <SeparatedList separator={<Divider />}>
        {renderNavigation()}
        {renderConversionElements()}
      </SeparatedList>
      {renderExchangeRate()}
      {renderPaymentServiceProviderFee()}
    </>
  )

  if (isMobileView) {
    return (
      <BottomSheet isVisible={isOpen} onClose={onClose}>
        {renderContent()}
      </BottomSheet>
    )
  }

  return (
    <Dialog show={isOpen}>
      {renderContent()}
      {renderActions()}
    </Dialog>
  )
}

export default PartialRefundOfferCurrencyConversionDialogNew
